(() => {
  if (customElements.get('glow-offer-carousel')) return;
  class GlowOfferCarousel extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      requestAnimationFrame(() => this.initialize());
    }
    initialize() {
      if (!this.isConnected) return;
      this.track = this.querySelector('.glow-offers__track');
      this.slides = [...this.querySelectorAll('[data-glow-slide]')];
      if (!this.track || !this.slides.length) {
        this.waiter = new MutationObserver(() => {
          if (this.querySelector('[data-glow-slide]')) { this.waiter.disconnect(); this.initialize(); }
        });
        this.waiter.observe(this, { childList: true, subtree: true });
        return;
      }
      const options = { signal: this.controller.signal };
      this.previous = this.querySelector('[data-glow-prev]');
      this.next = this.querySelector('[data-glow-next]');
      this.position = this.querySelector('[data-glow-position]');
      this.previous?.addEventListener('click', () => this.move(-1), options);
      this.next?.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => this.update(), { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          if (event.key === 'Home') this.show(0);
          else if (event.key === 'End') this.show(this.slides.length - 1);
          else this.move((event.key === 'ArrowRight' ? 1 : -1) * (this.rtl ? -1 : 1));
        }
      }, options);
      this.track.addEventListener('dragstart', event => event.preventDefault(), options);
      this.track.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.drag = { id: event.pointerId, x: event.clientX, scroll: this.track.scrollLeft, moved: false };
      }, options);
      window.addEventListener('pointermove', event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const distance = event.clientX - this.drag.x;
        if (Math.abs(distance) > 7) this.drag.moved = true;
        if (!this.drag.moved) return;
        event.preventDefault();
        this.track.classList.add('is-dragging');
        this.track.scrollLeft = this.drag.scroll - distance;
      }, options);
      const finish = event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        if (this.drag.moved) this.suppressClickUntil = performance.now() + 350;
        this.drag = null;
        this.track.classList.remove('is-dragging');
        this.update();
      };
      window.addEventListener('pointerup', finish, options);
      window.addEventListener('pointercancel', finish, options);
      this.track.addEventListener('click', event => {
        if (performance.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); event.stopImmediatePropagation(); }
      }, { ...options, capture: true });
      this.track.addEventListener('click', event => {
        if (event.target.closest('a,button')) return;
        event.target.closest('[data-glow-slide]')?.querySelector('[data-glow-buy]')?.click();
      }, options);
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.update();
    }
    disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); this.waiter?.disconnect(); }
    update() {
      const bounds = this.track.getBoundingClientRect();
      this.rtl = getComputedStyle(this.track).direction === 'rtl';
      const positions = this.slides.map((slide, index) => {
        const rect = slide.getBoundingClientRect();
        return { index, rect, distance: Math.abs(this.rtl ? rect.right - bounds.right : rect.left - bounds.left), overlap: Math.min(bounds.right, rect.right) - Math.max(bounds.left, rect.left) };
      });
      this.current = positions.reduce((a, b) => a.distance < b.distance ? a : b).index;
      const visible = positions.filter(item => item.overlap > Math.min(item.rect.width * .5, bounds.width * .5));
      const first = positions[0].rect;
      const last = positions.at(-1).rect;
      if (this.previous) this.previous.disabled = this.rtl ? first.right <= bounds.right + 3 : first.left >= bounds.left - 3;
      if (this.next) this.next.disabled = this.rtl ? last.left >= bounds.left - 3 : last.right <= bounds.right + 3;
      if (this.position && visible.length) this.position.textContent = `${visible[0].index + 1}${visible.length > 1 ? '–' + (visible.at(-1).index + 1) : ''} / ${this.slides.length}`;
    }
    move(direction) { this.update(); this.show(Math.max(0, Math.min(this.slides.length - 1, this.current + direction))); }
    show(index) {
      const bounds = this.track.getBoundingClientRect();
      const rect = this.slides[index].getBoundingClientRect();
      this.track.scrollBy({ left: this.rtl ? rect.right - bounds.right : rect.left - bounds.left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
  }
  customElements.define('glow-offer-carousel', GlowOfferCarousel);
})();
